import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import "vanilla-cookieconsent/dist/cookieconsent.css"
import * as CookieConsent from "vanilla-cookieconsent"

const SUPPORTED_LANGS = ["en", "ru", "bg", "ro", "uk", "fr"]

const getPrimaryDomain = () => window.location.hostname.replace(/^(test|www)\./, "")

const buildTranslations = (i18n) => {
  const domain = getPrimaryDomain()

  return SUPPORTED_LANGS.reduce((acc, lng) => {
    const t = i18n.getFixedT(lng)

    acc[lng] = {
      consentModal: {
        title: t("cookie_consent.consentModal.title"),
        description: t("cookie_consent.consentModal.description"),
        acceptAllBtn: t("cookie_consent.consentModal.acceptAllBtn"),
        acceptNecessaryBtn: t("cookie_consent.consentModal.acceptNecessaryBtn"),
        showPreferencesBtn: t("cookie_consent.consentModal.showPreferencesBtn"),
      },

      preferencesModal: {
        title: t("cookie_consent.preferencesModal.title"),
        acceptAllBtn: t("cookie_consent.preferencesModal.acceptAllBtn"),
        acceptNecessaryBtn: t("cookie_consent.preferencesModal.acceptNecessaryBtn"),
        savePreferencesBtn: t("cookie_consent.preferencesModal.savePreferencesBtn"),
        closeIconLabel: t("cookie_consent.preferencesModal.closeIconLabel"),

        sections: [
          {
            title: t("cookie_consent.privacyChoices.title"),
            description: t("cookie_consent.privacyChoices.description", { domain }),
          },

          {
            title: t("cookie_consent.necessary.title"),
            description: t("cookie_consent.necessary.description"),
            linkedCategory: "necessary",

            cookieTable: {
              headers: {
                name: t("cookie_consent.table.name"),
                domain: t("cookie_consent.table.domain"),
                desc: t("cookie_consent.table.desc"),
                expiration: t("cookie_consent.table.expiration"),
              },

              body: [
                {
                  name: "_session_id",
                  domain,
                  desc: t("cookie_consent.table.session_id_desc"),
                  expiration: t("cookie_consent.table.exp_session"),
                },
                {
                  name: "city_id",
                  domain,
                  desc: t("cookie_consent.table.city_id_desc"),
                  expiration: t("cookie_consent.table.exp_1_year"),
                },
                {
                  name: "lang",
                  domain,
                  desc: t("cookie_consent.table.lang_desc"),
                  expiration: t("cookie_consent.table.exp_session"),
                },
                {
                  name: "locale",
                  domain,
                  desc: t("cookie_consent.table.locale_desc"),
                  expiration: t("cookie_consent.table.exp_session"),
                },
                {
                  name: "referer",
                  domain,
                  desc: t("cookie_consent.table.referer_desc"),
                  expiration: t("cookie_consent.table.exp_1_year"),
                },
              ],
            },
          },

          // TODO: временно исключено — вернуть, когда снова понадобятся эти категории
          // {
          //   title: t("cookie_consent.performance.title"),
          //   description: t("cookie_consent.performance.description"),
          //   linkedCategory: "performance",
          // },
          //
          // {
          //   title: t("cookie_consent.functionality.title"),
          //   description: t("cookie_consent.functionality.description"),
          //   linkedCategory: "functionality",
          // },
          //
          // {
          //   title: t("cookie_consent.marketing.title"),
          //   description: t("cookie_consent.marketing.description"),
          //   linkedCategory: "marketing",
          // },
          //
          // {
          //   title: t("cookie_consent.advertising.title"),
          //   description: t("cookie_consent.advertising.description"),
          //   linkedCategory: "advertising",
          // },

          {
            title: t("cookie_consent.analytics.title"),
            description: t("cookie_consent.analytics.description"),
            linkedCategory: "analytics",

            cookieTable: {
              headers: {
                name: t("cookie_consent.table.name"),
                domain: t("cookie_consent.table.domain"),
                desc: t("cookie_consent.table.desc"),
                expiration: t("cookie_consent.table.expiration"),
              },

              body: [
                {
                  name: "_ga, _ga_*",
                  domain,
                  desc: t("cookie_consent.table.ga_desc"),
                  expiration: t("cookie_consent.table.exp_2_years"),
                },
                {
                  name: "_gid",
                  domain,
                  desc: t("cookie_consent.table.gid_desc"),
                  expiration: t("cookie_consent.table.exp_24h"),
                },
                {
                  name: "_gcl_au",
                  domain,
                  desc: t("cookie_consent.table.gcl_au_desc"),
                  expiration: t("cookie_consent.table.exp_3_months"),
                },
                {
                  name: "_clck, _clsk",
                  domain,
                  desc: t("cookie_consent.table.clarity_desc"),
                  expiration: t("cookie_consent.table.exp_1_year"),
                },
                {
                  name: "_fbp",
                  domain,
                  desc: t("cookie_consent.table.fbp_desc"),
                  expiration: t("cookie_consent.table.exp_3_months"),
                },
                {
                  name: "_hjSession_*, _hjSessionUser_*",
                  domain,
                  desc: t("cookie_consent.table.hotjar_desc"),
                  expiration: t("cookie_consent.table.exp_session_1_year"),
                },
                {
                  name: "_ym_d, _ym_isad, _ym_uid, _ym_visorc",
                  domain,
                  desc: t("cookie_consent.table.ym_desc"),
                  expiration: t("cookie_consent.table.exp_1_year"),
                },
                {
                  name: "gaconnector_*",
                  domain,
                  desc: t("cookie_consent.table.gaconnector_desc"),
                  expiration: t("cookie_consent.table.exp_1_year"),
                },
              ],
            },
          },
        ],
      },
    }

    return acc
  }, {})
}

// --- Аналитика грузится только после согласия на категорию "analytics" ---

const loadGTM = () => {
  if (window.__gtmLoaded || !window.__GTM_ID) return
  window.__gtmLoaded = true

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" })

  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${window.__GTM_ID}`
  document.head.appendChild(script)
}

const loadGA = () => {
  if (window.__gaLoaded || !window.__GA_ID) return
  window.__gaLoaded = true

  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${window.__GA_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function () {
    window.dataLayer.push(arguments)
  }
  window.gtag("js", new Date())
  window.gtag("config", window.__GA_ID)
}

const loadAmoSocialButton = () => {
  const cfg = window.__AMO_SOCIAL_BUTTON_CONFIG
  if (window.__amoSocialButtonLoaded || !cfg) return
  window.__amoSocialButtonLoaded = true

  window.amo_social_button = {
    id: cfg.id,
    hash: cfg.hash,
    locale: cfg.locale,
    inline: cfg.inline,
    setMeta: function (p) {
      this.params = (this.params || []).concat([p])
    },
  }

  window.amoSocialButton =
    window.amoSocialButton ||
    function () {
      ;(window.amoSocialButton.q = window.amoSocialButton.q || []).push(arguments)
    }

  const script = document.createElement("script")
  script.async = true
  script.id = "amo_social_button_script"
  script.src = cfg.scriptSrc || "https://gso.amocrm.ru/js/button.js"
  document.head.appendChild(script)
}

const loadClarity = () => {
  const id = window.__CLARITY_ID
  if (window.__clarityLoaded || !id) return
  window.__clarityLoaded = true
  ;(function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        ;(c[a].q = c[a].q || []).push(arguments)
      }
    t = l.createElement(r)
    t.async = 1
    t.src = "https://www.clarity.ms/tag/" + i
    y = l.getElementsByTagName(r)[0]
    y.parentNode.insertBefore(t, y)
  })(window, document, "clarity", "script", id)
}

const loadAnalyticsIfConsented = () => {
  if (CookieConsent.acceptedCategory("analytics")) {
    loadGTM()
    loadGA()
    loadAmoSocialButton()
    loadClarity()
  }
}

// --- Цвета темы: если themeColors пуст/не передан — стили кнопок/тумблеров не трогаем вообще ---

const buildThemeStyleBlock = (themeColors) => {
  const colors = themeColors && typeof themeColors === "object" ? themeColors : {}
  const hasAnyColor = Object.values(colors).some((v) => typeof v === "string" && v.trim() !== "")

  if (!hasAnyColor) {
    return ""
  }

  const primary = colors.primaryColor || null
  const secondary = colors.secondaryColor || null

  const btnBg = colors.buttonBgColor || primary
  const btnText = colors.buttonTextColor || null
  const btnHoverBg = colors.buttonHoverBgColor || secondary || btnBg
  const btnHoverText = colors.buttonHoverTextColor || btnText

  const lines = []

  if (btnBg) {
    lines.push(`--cc-btn-primary-bg: ${btnBg};`)
    lines.push(`--cc-btn-primary-border-color: ${btnBg};`)
    lines.push(`--cc-toggle-on-bg: ${btnBg};`)
  }
  if (btnText) {
    lines.push(`--cc-btn-primary-color: ${btnText};`)
  }
  if (btnHoverBg) {
    lines.push(`--cc-btn-primary-hover-bg: ${btnHoverBg};`)
    lines.push(`--cc-btn-primary-hover-border-color: ${btnHoverBg};`)
  }
  if (btnHoverText) {
    lines.push(`--cc-btn-primary-hover-color: ${btnHoverText};`)
  }

  if (lines.length === 0) {
    return ""
  }

  return lines.join("\n            ")
}

const CookieConsentWidget = ({ locale = "en", themeColors = {} }) => {
  const { i18n } = useTranslation()
  const [hasConsent, setHasConsent] = useState(false)

  useEffect(() => {
    let cancelled = false

    const initConsent = async () => {
      // Ждём реальной загрузки переводов в i18next (они могут тянуться асинхронно
      // с бэкенда), иначе t() на этот момент вернёт голые ключи вместо текста.
      try {
        await i18n.loadLanguages(SUPPORTED_LANGS)
      } catch (e) {
        // не блокируем инициализацию виджета целиком из-за одного языка —
        // на нём buildTranslations просто вернёт ключи, остальные останутся ок
      }

      if (cancelled) return

      const config = {
        guiOptions: {
          consentModal: {
            layout: "box",
            position: "bottom left",
            disablePageInteraction: true,
          },

          preferencesModal: {
            layout: "box",
          },
        },

        categories: {
          necessary: {
            enabled: true,
            readOnly: true,
          },
          // TODO: временно исключено — вернуть вместе с секциями выше
          // performance: {},
          // functionality: {},
          // marketing: {},
          // advertising: {},
          analytics: {
            autoClear: {
              cookies: [
                { name: /^_ga/ },
                { name: "_gid" },
                { name: "_gcl_au" },
                { name: /^_clck|_clsk/ },
                { name: "_fbp" },
                { name: /^_hjSession/ },
                { name: /^_ym_/ },
                { name: /^gaconnector_/ },
              ],
            },
          },
        },

        language: {
          default: locale,
          translations: buildTranslations(i18n),
        },

        onFirstConsent: ({ cookie }) => {
          setHasConsent(CookieConsent.validConsent())
          loadAnalyticsIfConsented()
        },

        onConsent: ({ cookie }) => {
          setHasConsent(CookieConsent.validConsent())
          loadAnalyticsIfConsented()
        },

        onChange: ({ cookie, changedCategories }) => {
          if (
            changedCategories.includes("analytics") &&
            !cookie.categories.includes("analytics") &&
            (window.__gtmLoaded ||
              window.__gaLoaded ||
              window.__amoSocialButtonLoaded ||
              window.__clarityLoaded)
          ) {
            window.location.reload()
            return
          }

          loadAnalyticsIfConsented()
        },
      }

      CookieConsent.run(config)

      window.CookieConsent = CookieConsent

      setHasConsent(CookieConsent.validConsent())
      loadAnalyticsIfConsented()
    }

    initConsent()

    return () => {
      cancelled = true
      document.body.classList.remove("overflow-hidden")
    }
  }, [locale, i18n])

  useEffect(() => {
    if (hasConsent) {
      document.body.classList.remove("overflow-hidden")
    } else {
      document.body.classList.add("overflow-hidden")
    }

    return () => {
      document.body.classList.remove("overflow-hidden")
    }
  }, [hasConsent])

  const themeStyleBlock = buildThemeStyleBlock(themeColors)

  return (
    <>
      <style>
        {`
          #cc-main {
            z-index: 2147483647 !important;
            ${themeStyleBlock}
          }
        `}
      </style>

      {!hasConsent && (
        <div
          aria-hidden="true"
          className="
            fixed
            inset-0
            pointer-events-auto
            select-none
            touch-none
          "
          style={{
            zIndex: 2147483646,
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(2px)",
          }}
        />
      )}
    </>
  )
}

export default CookieConsentWidget
